import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { auth } from "../firebase"; // importa o auth para pegar dados do usuário
import { motion } from "framer-motion";
import Question from "./Question";
import Result from "./Result";

const Quiz = ({ updateProgress }) => {
  const [searchParams] = useSearchParams();
  const temaParam = searchParams.get("tema");
  const [mode, setMode] = useState(temaParam === "ecologia" ? "ecologia" : "cultura");

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [quizFinished, setQuizFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [level, setLevel] = useState(1);
  const [hasNextLevel, setHasNextLevel] = useState(false);

  useEffect(() => {
    const novoModo = temaParam === "ecologia" ? "ecologia" : "cultura";
    if (novoModo !== mode) {
      setMode(novoModo);
      setLevel(1);
      setQuizFinished(false);
      setSelectedAnswer(null);
    }
  }, [temaParam]);

  useEffect(() => {
    fetchQuestions(level, mode);
  }, [level, mode]);

  useEffect(() => {
    updateProgress?.(currentQuestion, questions.length);
  }, [currentQuestion, questions, updateProgress]);

  const shuffleQuestions = (questions) => {
    let shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const fetchQuestions = async (currentLevel, currentMode) => {
    setLoading(true);
    try {
      const collectionName = currentMode === "cultura" ? "questions" : "ecoQuestions";
      const querySnapshot = await getDocs(
        query(collection(db, collectionName), where("level", "==", currentLevel))
      );

      const questionsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (questionsData.length > 0) {
        setQuestions(shuffleQuestions(questionsData));
        setCurrentQuestion(0);
        setCorrectCount(0);
        setWrongCount(0);
        setQuizFinished(false);
        setSelectedAnswer(null);
      } else {
        setQuizFinished(true);
      }

      checkNextLevel(currentLevel + 1, currentMode);
    } catch (error) {
      console.error("Erro ao carregar perguntas:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkNextLevel = async (nextLevel, currentMode) => {
    const collectionName = currentMode === "cultura" ? "questions" : "ecoQuestions";
    const querySnapshot = await getDocs(
      query(collection(db, collectionName), where("level", "==", nextLevel))
    );
    setHasNextLevel(!querySnapshot.empty);
  };

  const handleAnswer = (selectedOption) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(selectedOption);

    const correctAnswer = questions[currentQuestion]?.answer;
    if (selectedOption === correctAnswer) {
      setCorrectCount((prev) => prev + 1);
    } else {
      setWrongCount((prev) => prev + 1);
    }

    setTimeout(() => {
      nextQuestion();
    }, 1500);
  };

  const nextQuestion = () => {
    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      saveQuizResult(); // ⬅️ salvar antes de finalizar
      setQuizFinished(true);
    }
    setSelectedAnswer(null);
  };

  const advanceLevel = () => {
    if (hasNextLevel) {
      setLevel((prev) => prev + 1);
    } else {
      setQuizFinished(true);
    }
  };

  const restartQuiz = () => {
    setLevel(1);
    fetchQuestions(1, mode);
    setQuizFinished(false);
    setSelectedAnswer(null);
  };

  // 🔥 Salvar resultado no Firestore
  const saveQuizResult = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log("Usuário não autenticado, salvando como convidado");
        // Salvar como convidado se não estiver logado
        const guestName = localStorage.getItem("guestName") || "Convidado";
        const collectionName = mode === "ecologia" ? "ecoRanking" : "ranking";
        const rankingRef = collection(db, collectionName);

        await addDoc(rankingRef, {
          name: guestName,
          level,
          totalScore: correctCount,
          mode,
          date: serverTimestamp(),
          uid: `guest_${Date.now()}`, // ID único para convidados
          isGuest: true,
        });
        
        console.log("Resultado de convidado salvo com sucesso!");
        return;
      }

      const name = user.displayName || user.email?.split('@')[0] || "Usuário";
      const collectionName = mode === "ecologia" ? "ecoRanking" : "ranking";
      const rankingRef = collection(db, collectionName);

      const resultData = {
        name,
        level,
        totalScore: correctCount,
        mode,
        date: serverTimestamp(),
        uid: user.uid,
        isGuest: false,
        questionsAnswered: questions.length,
        correctAnswers: correctCount,
        wrongAnswers: wrongCount,
        accuracy: questions.length > 0 ? (correctCount / questions.length) * 100 : 0,
      };

      await addDoc(rankingRef, resultData);

      console.log("Resultado salvo com sucesso!", resultData);
    } catch (error) {
      console.error("Erro ao salvar resultado no ranking:", error);
      // Tentar salvar localmente como fallback
      try {
        const localResults = JSON.parse(localStorage.getItem("localResults") || "[]");
        localResults.push({
          name: "Usuário Local",
          level,
          totalScore: correctCount,
          mode,
          date: new Date().toISOString(),
          uid: `local_${Date.now()}`,
          isGuest: true,
        });
        localStorage.setItem("localResults", JSON.stringify(localResults));
        console.log("Resultado salvo localmente como fallback");
      } catch (localError) {
        console.error("Erro ao salvar localmente:", localError);
      }
    }
  };

  if (loading) {
    return <p style={{ textAlign: "center", marginTop: "2rem" }}>Carregando perguntas...</p>;
  }

  if (quizFinished) {
    return (
      <Result
        correctCount={correctCount}
        wrongCount={wrongCount}
        totalQuestions={questions.length}
        restartQuiz={restartQuiz}
        nextLevel={correctCount / questions.length >= 0.5 ? advanceLevel : null}
        level={level}
        hasNextLevel={hasNextLevel}
        mode={mode}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        padding: "1rem",
        textAlign: "center",
      }}
    >
      <h2 style={{ marginBottom: "1rem" }}>Nível {level}</h2>
      <Question
        question={questions[currentQuestion]?.question}
        options={questions[currentQuestion]?.options}
        correctAnswer={questions[currentQuestion]?.answer}
        handleAnswer={handleAnswer}
        selectedAnswer={selectedAnswer}
      />
    </motion.div>
  );
};

export default Quiz;
